"use client";

import React from "react";
import { RotateCw, AlertTriangle } from "lucide-react";

interface ErrorBlockProps {
  /** Short badge label, e.g. "feed unavailable". */
  badge?: string;
  /** One-line explanation of what failed. */
  message: string;
  /** Retry affordance; omit to hide the retry button. */
  onRetry?: () => void;
  className?: string;
}

/**
 * Shared error state — a destructive badge, a message, and an optional retry
 * button so a dropped feed reads as recoverable rather than blank.
 */
export function ErrorBlock({
  badge = "feed unavailable",
  message,
  onRetry,
  className,
}: ErrorBlockProps) {
  return (
    <div
      role="alert"
      className={`flex flex-1 flex-col items-center justify-center gap-2.5 px-3 py-6 text-center ${className ?? ""}`}
    >
      <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[9.5px] font-mono font-bold uppercase tracking-wide text-destructive">
        <AlertTriangle className="h-3 w-3" />
        {badge}
      </span>
      <p className="text-[11px] font-mono text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-mono text-foreground hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RotateCw className="h-3 w-3" />
          retry
        </button>
      )}
    </div>
  );
}

export default ErrorBlock;
